import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Mail, Loader2, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import GulfHeaderNav from "../../components/GulfHeaderNav";
import Logo from "../../components/Logo";
import { trackVisit, joinCommunity, trackCtaClick } from "../../lib/api";
import { toast } from "sonner";

export default function GulfCommunity() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { trackVisit("gulf-community"); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const cleaned = email.trim();
    if (!cleaned || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      toast.error("الرجاء كتابة بريد إلكتروني صحيح.");
      return;
    }
    setSubmitting(true);
    try {
      await joinCommunity(cleaned, name.trim() || null, "gulf-community");
      trackCtaClick("community-join-success");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "حدث خطأ أثناء التسجيل، حاول مجدداً.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#040914] text-white" dir="rtl" data-testid="gulf-community-root">
      <GulfHeaderNav variant="static" />

      <section className="py-14 sm:py-24">
        <div className="max-w-2xl mx-auto px-5 sm:px-6">
          {!done ? (
            <>
              <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-3 text-center">انضم لمجتمعنا</div>
              <h1 className="font-display text-3xl sm:text-5xl font-black text-center leading-tight">
                كن جزءاً من <span className="text-[#C5A059]">المجتمع</span>
              </h1>
              <p className="mt-5 text-sm sm:text-lg text-[#A1B0C6] leading-relaxed text-center">
                اشترك ليصلك أولاً كل محتوى جديد، نصائح عملية، وأسرار من الكتاب الأزرق — مجاناً، مباشرة على بريدك.
              </p>

              <form onSubmit={handleSubmit} className="mt-9 sm:mt-12 bg-[#0B1D3A]/70 border border-[#C5A059]/25 rounded-sm p-6 sm:p-8 space-y-4" dir="rtl" data-testid="community-form">
                <div>
                  <label htmlFor="name" className="block text-sm text-[#A1B0C6] mb-1.5">الاسم (اختياري)</label>
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسمك" data-testid="community-name-input" className="bg-[#040914] border-[#C5A059]/30 text-white placeholder:text-[#A1B0C6]/50" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm text-[#A1B0C6] mb-1.5">البريد الإلكتروني <span className="text-[#C5A059]">*</span></label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@email.com" data-testid="community-email-input" className="bg-[#040914] border-[#C5A059]/30 text-white placeholder:text-[#A1B0C6]/50" style={{ direction: "ltr", textAlign: "right" }} />
                </div>

                <Button type="submit" disabled={submitting} data-testid="community-submit-btn" className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold text-base sm:text-lg py-6 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.35)]">
                  {submitting ? (<span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> جاري التسجيل...</span>) : (<span className="flex items-center gap-2 justify-center"><Mail className="w-4 h-4" /> انضم الآن مجاناً</span>)}
                </Button>
                <p className="text-[11px] text-[#A1B0C6] text-center">لن نشارك بريدك مع أي طرف ثالث. يمكنك إلغاء الاشتراك في أي وقت.</p>
              </form>
            </>
          ) : (
            <div className="bg-[#0B1D3A]/80 border border-[#C5A059]/40 rounded-sm p-8 sm:p-12 text-center shadow-[0_0_60px_rgba(197,160,89,0.18)]" data-testid="community-success">
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/50 flex items-center justify-center">
                  <CheckCircle2 className="w-9 h-9 sm:w-11 sm:h-11 text-[#C5A059]" strokeWidth={1.8} />
                </div>
              </div>
              <h2 className="font-display text-2xl sm:text-4xl font-black">
                <span className="text-[#C5A059]">تم التسجيل</span> بنجاح
              </h2>
              <p className="mt-4 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
                أهلاً بك في مجتمع الكتاب الأزرق.
                <br/>سيصلك أول محتوى مجاني قريباً على بريدك:
                <br/><span className="text-white font-bold mt-2 inline-block" style={{ direction: "ltr" }}>{email}</span>
              </p>
              <Link to="/" data-testid="community-back-to-home">
                <Button className="mt-7 bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold rounded-sm px-7 py-5 text-sm sm:text-base">العودة للصفحة الرئيسية</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-[#C5A059]/15 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A1B0C6]">
          <Logo className="h-9 w-9" />
          <div>© {new Date().getFullYear()} الكتاب الأزرق</div>
          <div className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#C5A059]" /> دفع آمن</div>
        </div>
      </footer>
    </div>
  );
}
